import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	merge,
	Observable,
	Subject,
} from 'rxjs';
import { filter, map, shareReplay, startWith } from 'rxjs/operators';

import {
	CatalogItem,
	FilterState,
	ItemType,
	ProjectDisplayMode,
	ProjectViewMode,
	SortOption,
} from '../../domain/catalog-item.types';

const INITIAL_FILTER: FilterState = {
	search: '',
	types: new Set<ItemType>(),
	keywords: new Set<string>(),
	sortBy: 'date-desc',
	viewMode: 'cards',
	displayMode: 'flat',
	favoritesOnly: false,
	filtersEnabled: true,
};

const DEBOUNCE_TIMEOUT = 150;

@Injectable()
export class CatalogFilterService {
	private readonly allItemsSubject = new BehaviorSubject<Array<CatalogItem>>(
		new Array<CatalogItem>()
	);

	private readonly filterStateSubject = new BehaviorSubject<FilterState>({
		...INITIAL_FILTER,
	});

	// true when the change came from a control that was clicked rather than
	// typed into. Carries no state: subscribers read the current value, so a
	// debounced emission that lands late still reports what is true now.
	private readonly changes$ = new Subject<boolean>();

	public readonly allItems$ = this.allItemsSubject.asObservable();

	// Typing is coalesced, clicking is not: a view-mode or sort button has
	// nothing to wait for, and 150ms on a click reads as lag.
	//
	// Changes only, no opening value: what the user did and nothing else.
	public readonly filterChanges$: Observable<FilterState> = merge(
		this.changes$.pipe(filter((immediate) => immediate)),
		this.changes$.pipe(
			filter((immediate) => !immediate),
			debounceTime(DEBOUNCE_TIMEOUT)
		)
	).pipe(
		map(() => this.filterStateSubject.value),
		shareReplay({ bufferSize: 1, refCount: false })
	);

	// The same stream with an opening value, for everything that renders: a
	// view model needs something to show before the first click, and
	// filteredItems$ is a combineLatest that cannot emit without one.
	public readonly filterState$: Observable<FilterState> =
		this.filterChanges$.pipe(
			startWith(this.filterStateSubject.value),
			shareReplay({ bufferSize: 1, refCount: false })
		);

	public readonly keywords$ = this.allItemsSubject.pipe(
		map((items) => {
			const ks = new Set<string>();
			items.forEach((item) => item.keywords.forEach((t) => ks.add(t)));
			return [...ks].sort();
		})
	);

	public readonly filteredItems$ = combineLatest([
		this.allItemsSubject,
		this.filterState$,
	]).pipe(
		map(([items, state]) => {
			const { search, types, keywords, sortBy, filtersEnabled } = state;
			// Suspended: everything stays configured, nothing is applied.
			const q = filtersEnabled ? search.trim().toLowerCase() : '';

			const result = items.filter((item) => {
				if (!filtersEnabled) {
					return true;
				}
				if (types.size > 0 && !types.has(item.type)) {
					return false;
				}
				if (
					keywords.size > 0 &&
					![...keywords].some((t) => item.keywords.includes(t))
				) {
					return false;
				}
				if (q) {
					const inTitle = item.title.toLowerCase().includes(q);
					const inDesc = item.description.toLowerCase().includes(q);
					const inTags = item.keywords.some((t) => t.toLowerCase().includes(q));
					if (!inTitle && !inDesc && !inTags) {
						return false;
					}
				}
				return true;
			});

			return result.sort((a, b) => {
				switch (sortBy) {
					case 'date-asc':
						if (a.updatedOn.isSame(b.updatedOn)) {
							return 0;
						} else if (a.updatedOn.isBefore(b.updatedOn)) {
							return -1;
						} else {
							return 1;
						}
					case 'date-desc':
						if (b.updatedOn.isSame(a.updatedOn)) {
							return 0;
						} else if (b.updatedOn.isBefore(a.updatedOn)) {
							return -1;
						} else {
							return 1;
						}
					case 'title-asc':
						return a.title.localeCompare(b.title);
					case 'title-desc':
						return b.title.localeCompare(a.title);
					default:
						return 0;
				}
			});
		})
	);

	// Reads what is configured, not what is applied: a suspended filter set is
	// still clearable, and still re-activatable.
	public readonly hasActiveFilters$ = this.filterStateSubject.pipe(
		map(
			(s) =>
				!!s.search ||
				s.types.size > 0 ||
				s.keywords.size > 0 ||
				s.favoritesOnly
		)
	);

	// ── Mutators ──────────────────────────────────────────────────────────────
	public setItems(items: Array<CatalogItem>): void {
		this.allItemsSubject.next(items);
	}

	private push(next: FilterState, immediate = true): void {
		this.filterStateSubject.next(next);
		this.changes$.next(immediate);
	}

	public setSearch(value: string): void {
		this.push(
			{
				...this.filterStateSubject.value,
				search: value,
			},
			false
		);
	}

	public toggleType(type: ItemType): void {
		const types = new Set(this.filterStateSubject.value.types);
		if (types.has(type)) {
			types.delete(type);
		} else {
			types.add(type);
		}
		this.push({
			...this.filterStateSubject.value,
			types,
		});
	}

	public toggleTag(tag: string): void {
		const ks = new Set(this.filterStateSubject.value.keywords);
		if (ks.has(tag)) {
			ks.delete(tag);
		} else {
			ks.add(tag);
		}
		this.push({
			...this.filterStateSubject.value,
			keywords: ks,
		});
	}

	public setViewMode(viewMode: ProjectViewMode): void {
		if (this.filterStateSubject.value.viewMode === viewMode) {
			return;
		}
		this.push({
			...this.filterStateSubject.value,
			viewMode,
		});
	}

	public setDisplayMode(displayMode: ProjectDisplayMode): void {
		if (this.filterStateSubject.value.displayMode === displayMode) {
			return;
		}
		this.push({
			...this.filterStateSubject.value,
			displayMode,
		});
	}

	public setSortBy(sortBy: SortOption): void {
		this.push({
			...this.filterStateSubject.value,
			sortBy,
		});
	}

	public toggleFavoritesOnly(): void {
		this.push({
			...this.filterStateSubject.value,
			favoritesOnly: !this.filterStateSubject.value.favoritesOnly,
		});
	}

	public toggleFiltersEnabled(): void {
		this.push({
			...this.filterStateSubject.value,
			filtersEnabled: !this.filterStateSubject.value.filtersEnabled,
		});
	}

	public clearAll(): void {
		this.push({
			...INITIAL_FILTER,
			sortBy: this.filterStateSubject.value.sortBy,
			viewMode: this.filterStateSubject.value.viewMode,
			displayMode: this.filterStateSubject.value.displayMode,
		});
	}

	// ── Live data mutation helpers ────────────────────────────────────────────
	public addItem(item: CatalogItem): void {
		this.allItemsSubject.next([item, ...this.allItemsSubject.value]);
	}

	public removeItem(id: number): void {
		this.allItemsSubject.next(
			this.allItemsSubject.value.filter((i) => i.id !== id)
		);
	}
}
